import { useShallow } from 'zustand/react/shallow';
import { usePdfViewerStore } from './pdfViewer.store';
import { useTranslation } from 'react-i18next';

/**
 * Page indicator + jump-to-page input for the active PdfViewer. Renders
 * nothing until the viewer has loaded a document and registered its
 * goToPage handle. Renders bare — the caller owns the surrounding
 * `ControlGroup` pill, so the navigator can be merged into a shared pill
 * alongside the zoom controls.
 */
export function PdfPageNavigator() {
  const { t } = useTranslation('projects');

  const { currentPage, totalPages, goToPage } = usePdfViewerStore(
    useShallow((s) => ({
      currentPage: s.currentPage,
      totalPages: s.totalPages,
      goToPage: s.goToPage,
    })),
  );

  if (totalPages === 0) return null;

  const commit = (input: HTMLInputElement) => {
    const parsed = Number.parseInt(input.value, 10);
    const valid = !Number.isNaN(parsed) && parsed >= 1 && parsed <= totalPages;
    if (!valid) {
      input.value = String(currentPage);
      return;
    }
    if (parsed !== currentPage) goToPage(parsed);
  };

  return (
    <div className="flex items-center gap-2 px-4 py-2 text-sm tabular-nums text-foreground">
      <span>{t('projectDetail.pdfPageNavigator.page')}</span>
      <input
        key={currentPage}
        name="current-page"
        type="text"
        inputMode="numeric"
        aria-label={t('projectDetail.pdfPageNavigator.currentPage')}
        defaultValue={String(currentPage)}
        onFocus={(e) => e.currentTarget.select()}
        onBlur={(e) => commit(e.currentTarget)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            (e.currentTarget as HTMLInputElement).blur();
          } else if (e.key === 'Escape') {
            e.preventDefault();
            (e.currentTarget as HTMLInputElement).value = String(currentPage);
            (e.currentTarget as HTMLInputElement).blur();
          }
        }}
        style={{ width: `calc(${Math.max(String(totalPages).length, 1)}ch)` }}
        className="cursor-pointer rounded bg-transparent text-center text-foreground tabular-nums hover:underline focus:underline focus:outline-none"
      />
      <span>/</span>
      <span
        className="text-center"
        style={{ width: `calc(${Math.max(String(totalPages).length, 1)}ch)` }}
      >
        {totalPages}
      </span>
    </div>
  );
}
